import { ArrowLeft, Edit02, Loading01 } from "@untitledui/icons";
import { type FC, useMemo, useState } from "react";
import { useWorkspaceRisksList } from "@/api/workspace-risks";
import { useUpdateWorkspacePolicyTemplateSteps, useWorkspaceSafetyPolicyTemplates } from "@/api/workspace-safety";
import { PolicyRisksSidebar } from "@/components/safety-config/PolicyRisksSidebar";
import { POLICY_DESCRIPTIONS, POLICY_ICONS, POLICY_NAMES } from "@/components/safety-config/policy-copy";
import { POLICY_TOGGLEABLE_STEP_TYPES } from "@/components/safety-config/policy-toggleable-steps";
import { SequenceStepsPreview, type StepPatch } from "@/pages/sequences/sequence-steps-preview";
import { error as toastError } from "@/providers/ToastProvider";

type WorkspacePolicyDetailProps = {
  workspaceId: string;
  policyId: string;
  onBack: () => void;
};

/**
 * A workspace policy template, laid out like the merchant policy screen so both
 * read the same. Edits here only shape what a newly onboarded merchant starts
 * with — nothing runs at workspace level.
 */
export const WorkspacePolicyDetail = ({ workspaceId, policyId, onBack }: WorkspacePolicyDetailProps) => {
  const { data: policies } = useWorkspaceSafetyPolicyTemplates(workspaceId);
  const { data: workspaceRisks } = useWorkspaceRisksList(workspaceId);
  const update = useUpdateWorkspacePolicyTemplateSteps(workspaceId);
  const policy = policies?.find((p) => p.id === policyId);

  // Applied edits keyed by step number, so the preview reflects a change before
  // the refetch lands. Re-applying a patch the server already has is idempotent.
  const [patches, setPatches] = useState<Record<number, StepPatch>>({});

  const steps = policy?.steps ?? [];
  // A workspace has no merchant scope to resolve a message template from, so
  // these steps carry their copy inline and the preview always sees a null one.
  const editedSteps = useMemo(
    () => steps.map((step) => ({ ...step, messageTemplateId: null, ...patches[step.step] })),
    [steps, patches],
  );

  const mappedRisks = useMemo(
    () =>
      (workspaceRisks ?? [])
        .filter((wr) => wr.safetyPolicyType === policy?.type)
        .map((wr) => ({ id: wr.riskId, name: wr.risk.readableName, category: wr.risk.category })),
    [workspaceRisks, policy?.type],
  );

  // Commit a single step edit and persist it straight away, as the merchant
  // editor does — there's no explicit save button on either screen. The endpoint
  // patches the fields it receives, so only the edit itself travels.
  const handleStepChange = (stepNumber: number, patch: StepPatch) => {
    const { messageTemplateId: _unsupported, imageId: _image, imageUrl: _imageUrl, ...editable } = patch;
    setPatches((prev) => ({ ...prev, [stepNumber]: { ...prev[stepNumber], ...editable } }));

    update.mutate(
      { policyId, steps: [{ step: stepNumber, ...editable }] },
      { onError: () => toastError("Couldn't save policy changes") },
    );
  };

  if (!policy) {
    return (
      <div className="p-8">
        <button type="button" className="flex items-center gap-1.5 text-sm text-tertiary mb-6" onClick={onBack}>
          <ArrowLeft className="w-4 h-4" /> Back to policies
        </button>
        <p className="text-sm text-tertiary">Policy not found.</p>
      </div>
    );
  }

  const HeaderIcon: FC<{ className?: string; strokeWidth?: number }> = POLICY_ICONS[policy.type] ?? Edit02;

  return (
    <div className="flex flex-col">
      <button
        type="button"
        className="flex items-center gap-1.5 text-sm text-tertiary hover:text-secondary transition-colors mb-5 w-max"
        onClick={onBack}
      >
        <ArrowLeft className="w-4 h-4" /> Back to policies
      </button>

      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-normal text-primary flex items-center gap-2">
            <HeaderIcon className="w-5 h-5 text-quaternary" strokeWidth={1.8} />
            {POLICY_NAMES[policy.type]}
          </h1>
          <p className="text-sm text-quaternary mt-1">{POLICY_DESCRIPTIONS[policy.type]}</p>
        </div>
        {update.isPending && (
          <span className="flex items-center gap-1.5 text-xs text-tertiary shrink-0 mt-1">
            <Loading01 className="w-3.5 h-3.5 animate-spin" />
            Saving…
          </span>
        )}
      </div>

      <div className="flex flex-col lg:flex-row gap-6 items-start">
        <div className="flex-1 min-w-0">
          <SequenceStepsPreview
            type="safety"
            steps={editedSteps}
            triggerTitle="Risk accepted"
            triggerSubtitle="Risk is either confirmed by reviewer or auto-accepted"
            endLabel="Policy complete"
            editable
            onStepChange={handleStepChange}
            additionalToggleableStepTypes={POLICY_TOGGLEABLE_STEP_TYPES[policy.type]}
            merchantResources={false}
          />
        </div>
        <div className="w-full lg:w-80 shrink-0">
          <PolicyRisksSidebar risks={mappedRisks} />
        </div>
      </div>
    </div>
  );
};
